// ─────────────────────────────────────────────────────────────────────────────
// THE OFFER CATALOGUE — single source of truth for all 19 offers.
//
// Merged from two places that had drifted apart:
//   src/docs/offers-data.js   19 offers, catalogue schema, no symptoms
//   src/data/solutions.js      5 offers, page schema, symptoms and process
//
// Neither was complete. The catalogue is authoritative on WHAT an offer is —
// scope, timeline, price, inclusions, who it is for, which clients it proved on
// — and the page data is authoritative on everything needed to RENDER a page:
// symptoms, week-by-week process, commitments, what we need, FAQs, meta.
//
// Where the two disagreed on a shared field, the catalogue won. Recorded, since
// three of those are visible changes:
//
//   whoFor      The catalogue carries geography that solutions.js had dropped:
//               "US and UK installation...", "US private practices...",
//               "manufacturers in the US, UK and EU...". Three of five differed.
//   oneLiners   Offers 08 and 12 are TIERED and the catalogue says so in two
//               lines. solutions.js blended each pair into one sentence, and for
//               12 that sentence quoted $28,000 while priceDisplay read
//               "from $15,000". Both figures are correct — they are different
//               tiers — so the array form is kept and the singular `oneLiner`
//               is the tier that matches websiteDisplay.
//   included    Four entries differed by wording only, plus one real change:
//               offer 08 now names the carriers (UPS, DHL, USPS) rather than
//               saying "your regional carriers".
//
// solutions.js is now a filtered view of this file, so every existing consumer
// — Footer, SolutionsGrid, ServicePageOffers, both templates, the hub and
// /pricing/ — keeps working with no edit. `id` is preserved for that reason:
// Footer.js and SolutionsGrid.js look offers up by id, not slug.
//
// src/docs/offers-data.js stays in place as the reference document.
//
// CommonJS on purpose. gatsby-node.js requires this file, and Gatsby 3 runs
// gatsby-node.js as plain Node without transpiling what it requires, so ESM
// `export` syntax fails the build with "Unexpected token 'export'".
// ─────────────────────────────────────────────────────────────────────────────
//
// FIELD NOTES
//
//   live              A page exists at /solutions/{slug}/ or is being built.
//                     gatsby-node creates pages for live entries only. The 14
//                     that are false carry catalogue content but no page copy:
//                     process, commitments, weNeed, faqs and notIncluded are
//                     empty because the catalogue does not state them and they
//                     are not ours to invent (ARCHITECTURE §0).
//   symptoms          Section 1 of every offer page. The five live offers keep
//                     the arrays that were already in solutions.js verbatim.
//                     The other fourteen are drafted from that offer's own
//                     whoFor and included fields — the customer's words for a
//                     problem the offer already claims to solve. No new
//                     capability is introduced by any line.
//   proof.summary     The catalogue's prose, which names more clients than the
//                     structured form did.
//   proof.metrics     Structured only where solutions.js already had them.
//                     Splitting the catalogue prose into client + metrics for
//                     the other fourteen is left until each page is authored,
//                     so the split gets reviewed rather than assumed.
//   caseStudyPath     null unless a page actually exists and resolves. Verified
//                     by HTTP, not inferred from a filename. Five case studies
//                     name a client: bigJerrysFencing, nurturefulPsychology,
//                     emeAquaticSystems, muscatMediaGroup, orangelinePakistan.
//                     The six under /caseStudies/products/ name none.
//   servicePage       The parent service page (ARCHITECTURE §5, closes G6).
//   group             Catalogue letter A-F. displayGroup is the older word the
//                     /solutions/ hub already groups by; both are kept because
//                     they order the catalogue differently.
// ─────────────────────────────────────────────────────────────────────────────

const offers = [
  // ── A. Diagnostics ────────────────────────────────────────────────────────
  {
    num: "01",
    id: "fit-sprint",
    slug: "ops-and-erp-fit-sprint",
    name: "Ops & ERP Fit Sprint",
    group: "A",
    displayGroup: "entry",
    industry: "all",
    live: true,
    servicePage: null,
    h1: "Ops & ERP Fit Sprint — a costed roadmap in two weeks",
    metaTitle: "Ops & ERP Fit Sprint | Fixed $1,200, 2 Weeks | Visiomate",
    metaDescription:
      "Two weeks, $1,200 fixed. Get a process map, prioritised gap list, phased roadmap and a fixed-price quote you own — credited in full if you build with us.",
    oneLiner:
      "A costed, phased roadmap for your ERP or platform build, delivered in two weeks for a fixed $1,200 — credited back in full if you build with us.",
    oneLiners: [
      "A costed, phased roadmap for your ERP or platform build, delivered in two weeks for a fixed $1,200 — credited back in full if you build with us.",
    ],
    whoFor:
      "Any SMB weighing an ERP, platform or automation build and tired of vague agency proposals.",
    timeline: "2 weeks",
    timelineShort: "2 weeks",
    price: "$1,200 fixed — credited in full against any build",
    websiteDisplay: "$1,200 exact",
    priceDisplay: "$1,200 fixed",
    priceShort: "$1,200",
    priceDetail:
      "$1,200, payable in full at kickoff. Credited against phase one if you build with us.",
    symptoms: [
      "You've had three proposals and none of them agree on scope, cost or timeline",
      "You can't tell whether you need a new system or a fix to the one you have",
      "Every vendor wants a discovery phase before they'll quote anything",
      "Your team disagrees about what's actually broken",
      "You need a number you can take to your board",
    ],
    included: [
      "Process map of your current lead-to-cash or core operational flow",
      "Prioritised gap list with the cost of each gap left unfixed",
      "Phased delivery roadmap with sequencing and dependencies",
      "Fixed-price quote for phase one",
      "All artefacts are yours to keep and to shop elsewhere",
    ],
    notIncluded: ["Any build or configuration work", "Software licences", "Data migration"],
    // Two weeks, five stages, all five deliverables intact. Week one is the
    // field work — interviews, mapping, analysis; week two is synthesis,
    // costing and handover. Two stages share a week label on the same pattern
    // the AI Voice Receptionist (offer 04) already uses for a 2-week offer.
    process: [
      { step: "Week 1", title: "Kickoff and discovery", deliverable: "Stakeholder interviews, systems inventory" },
      { step: "Week 1", title: "Process mapping", deliverable: "Current-state process map" },
      { step: "Week 1", title: "Gap analysis", deliverable: "Prioritised gap list with cost of inaction" },
      { step: "Week 2", title: "Roadmap and costing", deliverable: "Phased roadmap, fixed-price quote" },
      { step: "Week 2", title: "Presentation and handover", deliverable: "Walkthrough session, all artefacts delivered" },
    ],
    commitments: [
      "All five deliverables handed over by the end of week two",
      "Everything produced is yours to keep, with no obligation to continue",
      "The full $1,200 is credited if you proceed with any build offer",
    ],
    weNeed: [
      "One named decision-maker available for the kickoff and handover sessions",
      "Access to the people who actually run the process day to day",
      "Read access to current systems and a sample of real data",
    ],
    // No proof section on this offer, deliberately.
    //
    // The catalogue line — "The front end of every Visiomate engagement" — is
    // positioning, not evidence: there is no client and no measurement behind
    // it. Carried as `client` and a `metric` it rendered a Proof section headed
    // "What changed for a business like yours", attributed to "EVERY VISIOMATE
    // ENGAGEMENT", with a signal rule above a sentence that is not a figure.
    // An empty proof block is worse than no proof block — it advertises that
    // there is nothing to show.
    //
    // `summary` keeps the catalogue's wording; it is not rendered as proof.
    proof: {
      summary: "The front end of every Visiomate engagement",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [
      { q: "What if we decide not to build with you?", a: "You keep everything. The process map, gap list, roadmap and quote are yours, and you're free to take them to another vendor. We'd rather you make a good decision than an uninformed one." },
      { q: "Why charge for this when other agencies scope for free?", a: "Free scoping is paid for somewhere — usually in inflated build quotes or thin discovery. A paid sprint means senior people spend real time on your problem and you get artefacts you can act on." },
      { q: "Is $1,200 really the total?", a: "Yes. Fixed, payable at kickoff, no change orders. If the scope turns out to be far larger than two weeks can cover, we tell you before you pay." },
      { q: "How soon can you start?", a: "Typically within one week of agreement." },
      { q: "Do we need to have chosen a system already?", a: "No. Deciding whether you need a new system at all is one of the questions the sprint answers." },
    ],
    related: ["odoo-for-manufacturers", "field-service-installer-platform"],
    note: null,
  },

  {
    num: "02",
    id: "erp-rescue-sprint",
    slug: "erp-rescue-sprint",
    name: "ERP Rescue Sprint",
    group: "A",
    displayGroup: "entry",
    industry: "all",
    live: true,
    servicePage: "/dynamics365/financeOperations",
    h1:
      "Find out why your ERP stalled, and what it costs to finish or replace it — in three weeks",
    metaTitle: "ERP Rescue Sprint | Fixed $3,500, 3 Weeks | Visiomate",
    metaDescription:
      "Three weeks, $3,500 fixed. An audit of your stalled Odoo or Dynamics rollout, a root-cause report and fixed-price options to finish it or replace it.",
    oneLiner:
      "A full diagnosis and stabilisation of your stalled Odoo or Dynamics rollout, delivered in three weeks for a fixed $3,500.",
    oneLiners: [
      "A full diagnosis and stabilisation of your stalled Odoo or Dynamics rollout, delivered in three weeks for a fixed $3,500.",
    ],
    whoFor:
      "Businesses with a stalled, abandoned or underperforming ERP where budget is already spent.",
    timeline: "3 weeks",
    timelineShort: "3 weeks",
    price: "$3,500 fixed",
    websiteDisplay: "$3,500 exact",
    priceDisplay: "$3,500 fixed",
    priceShort: "$3,500",
    priceDetail: null,
    symptoms: [
      "You paid for an ERP rollout and it stopped somewhere short of live",
      "Nobody can tell you whether the software is wrong or the implementation was",
      "Your team has gone back to spreadsheets while the system sits half-configured",
      "The data in it no longer matches the data you run the business on",
      "You need to know whether to finish it, replace it or stop — and what each costs",
    ],
    included: [
      "Configuration and data-integrity audit of the existing system",
      "Root-cause report separating product limits from implementation error",
      "Stabilisation of the highest-impact breakages inside the sprint",
      "Remediation plan with fixed-price options to finish or replace",
      "Honest recommendation, including when the answer is to stop",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary: "MIA Group (distressed ERP rescue) · TTI Testing Laboratories · Hush Puppies",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [],
    related: ["ops-and-erp-fit-sprint", "platform-modernization-assessment"],
    note: null,
  },

  {
    num: "03",
    id: "platform-modernization",
    slug: "platform-modernization-assessment",
    name: "Platform Modernization Assessment",
    group: "A",
    displayGroup: "entry",
    industry: "software",
    live: true,
    servicePage: "/dynamics365/financeOperations",
    h1:
      "Rebuild, refactor or replace your legacy platform — a costed decision in two weeks",
    metaTitle: "Modernization Assessment | Fixed $1,500, 2 Weeks | Visiomate",
    metaDescription:
      "Two weeks, $1,500 fixed. A codebase and architecture review, a rebuild-or-refactor recommendation with cost ranges, and a roadmap that keeps your product live.",
    oneLiner:
      "A rebuild, refactor or replace decision for your legacy platform, backed by real costings, delivered in two weeks for a fixed $1,500.",
    oneLiners: [
      "A rebuild, refactor or replace decision for your legacy platform, backed by real costings, delivered in two weeks for a fixed $1,500.",
    ],
    whoFor:
      "Software businesses and operators running a legacy platform that is expensive to change.",
    timeline: "2 weeks",
    timelineShort: "2 weeks",
    price: "$1,500 fixed",
    websiteDisplay: "$1,500 exact",
    priceDisplay: "$1,500 fixed",
    priceShort: "$1,500",
    priceDetail: null,
    symptoms: [
      "Every change to the platform takes longer and costs more than the last one",
      "One person understands how it works and you cannot replace them",
      "You are guessing whether to rebuild, refactor or start again",
      "You cannot get a straight number for what a rebuild would cost",
      "It has to keep running for customers while you decide",
    ],
    included: [
      "Codebase, architecture and dependency review",
      "Rebuild vs. refactor vs. replace recommendation with cost ranges",
      "Risk register covering security, scaling and key-person exposure",
      "Phased modernization roadmap that keeps the product live throughout",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary: 'Net Atlantic "NextMail" modernization (US) — 35% faster campaign deployment',
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [],
    related: ["erp-rescue-sprint", "embedded-delivery-pod"],
    // BLOCKING before this goes public. Carried through from the catalogue.
    note:
      "Margin review flags this at 1.43x internal cost, below our 2.0x floor. Either raise to $2,200 or reduce delivery to 1.5 weeks before this goes public.",
  },

  // ── B. AI ─────────────────────────────────────────────────────────────────
  {
    num: "04",
    id: "ai-voice-receptionist",
    slug: "ai-voice-receptionist",
    name: "AI Voice Receptionist",
    group: "B",
    displayGroup: "ai",
    industry: "field-service",
    live: true,
    servicePage: "/services/aisolution",
    h1: "AI Voice Receptionist — live in two weeks, answering every call",
    metaTitle: "AI Voice Receptionist for Home Services | Live in 2 Weeks | Visiomate",
    // Price removed — this offer is gated. Was: "...Live in two weeks. $3,000
    // setup, $400/month."
    metaDescription:
      "An AI receptionist that answers 24/7, qualifies callers and writes leads straight into your CRM. Live in two weeks, tuned monthly against real calls.",
    // Was: "...live in two weeks for $3,000, then $400 a month."
    oneLiner:
      "An AI receptionist that answers every call 24/7, qualifies the caller and writes the lead straight into your CRM — live in two weeks.",
    oneLiners: [
      "An AI receptionist that answers every call 24/7, qualifies the caller and writes the lead straight into your CRM — live in two weeks.",
    ],
    whoFor:
      "Home services, trades, clinics and restaurants losing revenue to unanswered calls.",
    timeline: "2 weeks to live",
    timelineShort: "2 weeks",
    price: "$3,000 setup + $400/month",
    websiteDisplay: "$3,000 + $400/mo — exact",
    priceDisplay: "$3,000 setup + $400/month",
    priceShort: "$3,000 + $400/mo",
    priceDetail:
      "$3,000 setup, payable 50% at kickoff and 50% at go-live. Then $400/month including 500 call minutes; additional minutes at $0.20.",
    symptoms: [
      "Calls go to voicemail after hours and those callers ring your competitor next",
      "Your office staff are interrupted all day by calls that aren't qualified leads",
      "You have no record of how many enquiries you actually receive",
      "Leads get written on paper and never make it into a system",
      "You've considered an answering service but they don't know your business",
    ],
    included: [
      "Voice agent trained on your services, pricing rules and service area",
      "Call qualification and intelligent routing to a human when needed",
      "Automatic lead creation in Odoo, HubSpot or your existing CRM",
      "Call transcripts, recordings and outcome dashboard",
      // Rate removed — this offer is gated, and the included list renders on
      // the page. Was: "500 call minutes included each month, then $0.20 per
      // minute". The allowance and the fact that overage is billed per minute
      // are the substance; the rate itself belongs in the scope document with
      // the rest of the pricing.
      "500 call minutes included each month, with additional minutes billed per minute",
      "Monthly tuning against real call data",
    ],
    notIncluded: [
      "Outbound calling campaigns",
      "Telephone number provisioning in regions we don't already cover",
      "CRM licence costs",
    ],
    process: [
      { step: "Week 1", title: "Capture and build", deliverable: "Call flows mapped, agent trained on your service catalogue and pricing rules" },
      { step: "Week 1", title: "CRM wiring", deliverable: "Lead creation, field mapping and routing rules connected to your CRM" },
      { step: "Week 2", title: "Test calls and tuning", deliverable: "Live test calls against real scenarios, responses tuned" },
      { step: "Week 2", title: "Go-live", deliverable: "Number routed, agent answering live traffic, dashboard handed over" },
    ],
    commitments: [
      "Answering live calls within two weeks of kickoff",
      "Every call transcribed and logged, with a lead in your CRM",
      "Monthly tuning included for as long as you subscribe",
    ],
    weNeed: [
      "Your service list, service area and any pricing rules the agent should know",
      "CRM access with permission to create leads",
      "Control of the phone number you want routed, or willingness to forward it",
    ],
    proof: {
      summary: "Big Jerry's Fencing (US) — 24/7 voice agent creating Odoo leads",
      client: "Big Jerry's Fencing",
      metrics: [
        "24/7 voice agent creating leads directly in Odoo",
        "21 cities across 9 US states",
      ],
      caseStudyPath: "/caseStudies/projects/bigJerrysFencing",
    },
    faqs: [
      { q: "Will callers know they're talking to an AI?", a: "Yes — we recommend the agent identifies itself. Callers accept it readily when the agent is genuinely useful and hands over to a human on request." },
      { q: "What happens if the agent can't answer something?", a: "It routes to a human on your defined rules — by time of day, topic or caller request — and logs what it couldn't answer so we can tune it." },
      // Rate removed — the FAQ answers are published in FAQPage JSON-LD, which
      // would otherwise carry a price for a gated offer. Was: "Additional
      // minutes are $0.20. Most single-location..."
      { q: "What if we get far more than 500 minutes?", a: "Additional minutes are billed at the per-minute rate in your scope document. Most single-location businesses stay inside the included allowance; we'll review usage in month one and tell you where you'll land." },
      { q: "Which CRMs do you support?", a: "Odoo and HubSpot out of the box. Others are usually straightforward — tell us what you run and we'll confirm before you commit." },
      { q: "Can we cancel?", a: "The monthly fee is rolling with 30 days' notice. The setup fee is not refundable once the agent is built." },
    ],
    related: ["field-service-installer-platform", "ai-quote-and-document-automation"],
    note:
      "The 500-minute cap is what keeps this fee profitable. Do not quote unlimited minutes at $400.",
  },

  {
    num: "05",
    id: "ai-catalog-assistant",
    slug: "ai-catalog-and-docs-assistant",
    name: "AI Catalog & Docs Assistant",
    group: "B",
    displayGroup: "ai",
    industry: "distribution",
    live: true,
    servicePage: "/services/aisolution",
    h1:
      "An assistant that answers catalogue and SOP questions for you — live in three weeks",
    metaTitle: "AI Catalog & Docs Assistant | Live in 3 Weeks | Visiomate",
    metaDescription:
      "An AI assistant grounded in your own catalogue and documentation, answering customer and staff questions with source citations. Live in three weeks.",
    oneLiner:
      "An AI assistant your customers and staff can ask about your entire product catalogue or internal SOPs — live in three weeks.",
    oneLiners: [
      "An AI assistant your customers and staff can ask about your entire product catalogue or internal SOPs — live in three weeks.",
    ],
    whoFor:
      "Distributors, manufacturers and service firms with large catalogues or dense internal documentation.",
    timeline: "3 weeks to live",
    timelineShort: "3 weeks",
    price: "$4,500 setup + $300/month",
    websiteDisplay: "$4,500 + $300/mo — exact",
    priceDisplay: "$4,500 setup + $300/month",
    priceShort: "$4,500 + $300/mo",
    priceDetail: null,
    symptoms: [
      "Customers ask which product fits and your team answers the same questions all day",
      "Your catalogue is too large for anyone to hold in their head",
      "New staff spend weeks learning where the answers live",
      "Your product data changes and the answers people give go out of date",
      "You have no record of what customers asked and did not get answered",
    ],
    included: [
      "Retrieval-augmented assistant grounded in your own content",
      "Website widget and/or internal staff interface",
      "Continuous ingestion so answers track your live catalogue",
      "Source citations on every answer and an escalation path to a human",
      "Unanswered-question log so you can see what customers actually want",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary: "Odoo-catalogue chat assistant with continuous product ingestion",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [],
    related: ["ai-quote-and-document-automation", "ai-voice-receptionist"],
    note: null,
  },

  {
    num: "06",
    id: "ai-quote-automation",
    slug: "ai-quote-and-document-automation",
    name: "AI Quote & Document Automation",
    group: "B",
    displayGroup: "ai",
    industry: "all",
    live: true,
    servicePage: "/services/aisolution",
    h1:
      "Quotes and contracts generated from your CRM data — live in four weeks",
    metaTitle: "AI Quote & Document Automation | Live in 4 Weeks | Visiomate",
    metaDescription:
      "Quotes, contracts and statements of work generated from live CRM and pricing data, with margin checks before anything is sent. Live in four weeks.",
    oneLiner:
      "Quotes and contracts generated automatically from your CRM data, with margin checks before anything goes out — live in four weeks.",
    oneLiners: [
      "Quotes and contracts generated automatically from your CRM data, with margin checks before anything goes out — live in four weeks.",
    ],
    whoFor:
      "Firms producing high volumes of quotes, contracts or statements of work by hand.",
    timeline: "4 weeks to live",
    timelineShort: "4 weeks",
    price: "$6,000 setup + $250/month",
    websiteDisplay: "$6,000 + $250/mo — exact",
    priceDisplay: "$6,000 setup + $250/month",
    priceShort: "$6,000 + $250/mo",
    priceDetail: null,
    symptoms: [
      "Quotes are rebuilt by hand from the same information every time",
      "Two people quote the same job and produce two different documents",
      "A deal is signed before anyone checks whether the margin survives",
      "Nobody is sure which version of the contract the customer actually has",
      "Approvals happen over email and leave no trail",
    ],
    included: [
      "Document generation engine driven by live CRM and pricing data",
      "Your templates, clause library and approval rules encoded",
      "E-signature and approval workflow",
      "Margin checks that flag a deal before it is sent, not after",
      "Version control and full audit trail",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary:
        "Centric Events (US) — document generation engine, zero manual re-entry from lead to invoice",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [],
    related: ["ai-catalog-and-docs-assistant", "odoo-for-events-and-production"],
    note: null,
  },

  // ── C. Odoo ───────────────────────────────────────────────────────────────
  {
    num: "07",
    id: "odoo-commerce",
    slug: "odoo-for-commerce-and-payments",
    name: "Odoo for Commerce & Payments",
    group: "C",
    displayGroup: "odoo",
    industry: "retail",
    // Backlink to the industry page this offer is featured on
    // (src/pages/industries/retail.js), per the industries rebuild's
    // cross-linking fix. Rendered in the Related section below.
    industryPages: [{ href: "/industries/retail", label: "Retail" }],
    live: true,
    servicePage: "/Odoo/implementationPartner",
    h1:
      "Storefront, stock and accounts in one system, for online retailers — live in four weeks",
    metaTitle: "Odoo for Commerce & Payments | Live in 4 Weeks | Visiomate",
    metaDescription:
      "Odoo eCommerce with Stripe or GoCardless, inventory and fulfilment, and accounting reconciled to payments. Built for D2C brands. Live in four weeks.",
    oneLiner:
      "An online store with payments, stock and accounting wired together in one system — live in four weeks.",
    oneLiners: [
      "An online store with payments, stock and accounting wired together in one system — live in four weeks.",
    ],
    whoFor:
      "Online retailers and D2C brands in the US, UK and EU outgrowing a disconnected storefront.",
    timeline: "4 weeks",
    timelineShort: "4 weeks",
    price: "from $8,500",
    websiteDisplay: "from $8,500",
    priceDisplay: "from $8,500",
    priceShort: "from $8,500",
    priceDetail: null,
    symptoms: [
      "Your storefront, your stock and your accounts are three systems that disagree",
      "You sell something you do not have because stock updates overnight, not live",
      "Someone reconciles payouts against orders by hand every month",
      "Refunds and failed payments are tracked in a spreadsheet",
      "Adding a channel means another integration nobody wants to own",
    ],
    included: [
      "Odoo eCommerce storefront configured to your catalogue",
      "Stripe, GoCardless or equivalent payment and direct-debit setup",
      "Inventory, fulfilment and shipping flows",
      "Accounting configured from the ground up, reconciled to payments",
      "Team training and a supported go-live",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary:
        "RenewellWater (UK) — Odoo 19 store with Stripe and GoCardless, accounting rebuilt from scratch",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [],
    related: ["odoo-for-multi-store-retail", "ops-and-erp-fit-sprint"],
    note: null,
  },

  {
    num: "08",
    id: "odoo-manufacturers",
    slug: "odoo-for-manufacturers",
    name: "Odoo for Manufacturers",
    group: "C",
    displayGroup: "odoo",
    industry: "manufacturing",
    industryPages: [{ href: "/industries/manufacturing", label: "Manufacturing" }],
    live: true,
    servicePage: "/Odoo/implementationPartner",
    h1: "Odoo for Manufacturers — production, BOMs and shipping live in weeks",
    // Price removed from the title — this offer is gated, and a SERP listing
    // reading "| From $11,000 |" would advertise a figure the page no longer
    // shows. Was: "Odoo Implementation for Manufacturers | From $11,000 |
    // Visiomate".
    metaTitle: "Odoo for Manufacturers | Fixed Scope, 5 Weeks | Visiomate",
    // Was: "...Core in 5 weeks from $11,000."
    metaDescription:
      "Fixed-scope Odoo for small and mid-sized manufacturers. BOMs, batch traceability, tiered quoting and carrier-integrated shipping. Core live in five weeks.",
    // Tiered. The singular line is Core. Prices removed from both.
    oneLiner:
      "Core — Production, BOMs and inventory running properly in Odoo for your factory: live in five weeks.",
    oneLiners: [
      "Core — Production, BOMs and inventory running properly in Odoo for your factory: live in five weeks.",
      "Complete — A full manufacturing system with batch traceability, tiered quoting and carrier-integrated shipping: live in eight weeks.",
    ],
    whoFor:
      "Small and mid-sized manufacturers in the US, UK and EU running production on spreadsheets or a legacy ERP.",
    timeline: "Core 5 weeks · Complete 8 weeks",
    timelineShort: "5–8 weeks",
    price: "Core from $11,000 · Complete from $16,000",
    websiteDisplay: "from $11,000",
    priceDisplay: "from $11,000",
    priceShort: "from $11,000",
    priceDetail:
      "Core from $11,000 (5 weeks). Complete from $16,000 (8 weeks). Payment 40% at kickoff, 30% at midpoint, 30% at go-live.",
    symptoms: [
      "Your BOMs live in spreadsheets and nobody trusts the current version",
      "You can't trace which batch went into which shipment",
      "Quoting takes days because costing is manual",
      "Someone re-keys tracking numbers into the courier site every afternoon",
      "You find out a job lost money after it shipped",
    ],
    included: [
      "Core: Manufacturing (MRP), BOMs, routings, purchase and inventory",
      "Core: production dashboards and role-based training",
      "Complete adds: batch and lot traceability end to end",
      "Complete adds: quotation portal with tiered pricing",
      "Complete adds: carrier integration and shipping automation (UPS, DHL, USPS)",
      "Complete adds: margin and cost-variance reporting",
    ],
    notIncluded: [
      "Odoo licence fees (paid directly to Odoo)",
      "Hardware, scanners or label printers",
      "Shop-floor IoT or machine integration",
    ],
    process: [
      { step: "Week 1", title: "Discovery and data audit", deliverable: "Process map, data quality report, configuration plan" },
      { step: "Week 2", title: "Core manufacturing config", deliverable: "MRP, BOMs and routings configured against your products" },
      { step: "Week 3", title: "Purchase, inventory, warehouse", deliverable: "Procurement and stock flows live in test" },
      { step: "Week 4", title: "Quotation portal and pricing", deliverable: "Tiered quoting configured and tested" },
      { step: "Week 5", title: "Shipping automation", deliverable: "Carrier integration live, manual tracking entry eliminated" },
      { step: "Week 6", title: "Data migration and UAT", deliverable: "Live data loaded, user acceptance testing signed off" },
      { step: "Week 7", title: "Training", deliverable: "Role-based training for production, purchasing and finance" },
      { step: "Week 8", title: "Go-live and hypercare", deliverable: "Production cutover with daily support through week one" },
    ],
    commitments: [
      "Live on the date agreed at kickoff, or we keep working at no extra cost",
      "The team that starts your project finishes it",
      "30-day post-launch warranty on everything we configure or build",
    ],
    weNeed: [
      "A named project owner with authority to sign off configuration decisions",
      "Feedback within 48 hours at each review point",
      "Product, BOM and customer data in any structured format by the date agreed at kickoff",
    ],
    proof: {
      summary:
        "EcoZohm (US, LED lighting) — full lifecycle live in 7 weeks, dual-tier quotes at 99% accuracy, manual tracking-number entry eliminated · Cakes & Bakes — precise BOMs and automated batch tracking",
      client: "EcoZohm (US, LED lighting)",
      metrics: [
        "7 weeks to a live quote-to-shipment lifecycle",
        "99% accuracy on dual-tier quotations",
        "Manual tracking-number entry eliminated entirely",
      ],
      // No case study page for EcoZohm. The PDF at
      // src/files/Visiomate_EcoZohm_Case_Study.pdf is not a route.
      caseStudyPath: null,
    },
    faqs: [
      { q: "What's the difference between Core and Complete?", a: "Core gets production, BOMs, purchasing and inventory running correctly in five weeks. Complete adds batch traceability, the tiered quotation portal, carrier-integrated shipping and margin reporting over eight weeks. Most manufacturers start with Core." },
      { q: "Why is this cheaper than other Odoo partners?", a: "Because we've built this configuration before. The second implementation in a vertical costs us far less than the first, and we pass that on. It is not a cheaper team — it is the same senior team on a route they already know." },
      { q: "Does the price include Odoo licences?", a: "No. Odoo licences are paid directly to Odoo and depend on your user count and apps. We'll tell you what to expect during the Fit Sprint." },
      { q: "What if our BOMs are a mess?", a: "That's normal, and the week-one data audit exists to find out how bad. If cleanup is larger than the plan allows, we quote it separately before starting rather than discovering it at week six." },
      { q: "How do you hold a fixed timeline?", a: "By fixing scope, naming one decision-maker, and requiring 48-hour feedback. If those conditions slip, the clock pauses — but the price doesn't move." },
    ],
    related: ["ops-and-erp-fit-sprint", "ai-quote-and-document-automation"],
    note: null,
  },

  {
    num: "09",
    id: "odoo-multi-store",
    slug: "odoo-for-multi-store-retail",
    name: "Odoo for Multi-Store Retail",
    group: "C",
    displayGroup: "odoo",
    industry: "retail",
    industryPages: [{ href: "/industries/retail", label: "Retail" }],
    live: true,
    servicePage: "/Odoo/implementationPartner",
    h1:
      "Every outlet, your warehouse and your online channel on one system — live in five to eight weeks",
    metaTitle: "Odoo for Multi-Store Retail | Live in 5-8 Weeks | Visiomate",
    metaDescription:
      "POS across every outlet, unified multi-location stock, marketplace sync and store-level reporting for retail chains and franchises. Core live in five weeks.",
    oneLiner:
      "Core — POS and unified stock across all your outlets: live in five weeks.",
    oneLiners: [
      "Core — POS and unified stock across all your outlets: live in five weeks.",
      "Complete — Every store, your warehouse and your online channel on one system, with store-level reporting and a faster month-end close: live in eight weeks.",
    ],
    whoFor: "Retail chains and franchises running 3–150+ outlets alongside an online channel.",
    timeline: "Core 5 weeks · Complete 8 weeks",
    timelineShort: "5–8 weeks",
    price: "Core from $11,000 · Complete from $16,000",
    websiteDisplay: "from $11,000",
    priceDisplay: "from $11,000",
    priceShort: "from $11,000",
    priceDetail: null,
    symptoms: [
      "Each outlet knows its own stock and nobody knows the total",
      "The tills stop taking money when the internet drops",
      "Transfers between stores are agreed on the phone and recorded later, if at all",
      "Your online channel sells stock the shops already sold",
      "Month-end close takes weeks because every store reports differently",
    ],
    included: [
      "Core: POS deployment across outlets, including offline-tolerant operation",
      "Core: unified multi-location inventory with transfers and stock visibility",
      "Complete adds: eCommerce and marketplace synchronisation",
      "Complete adds: store, region and product-level reporting",
      "Complete adds: standardised financial reporting and faster month-end close",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary: "Borjan (145+ outlets) · Hush Puppies — Shopify-to-ERP automation · VisioEdge POS",
      client: null,
      metrics: [],
      // /caseStudies/products/visioedge/ resolves but names no client, so it is
      // a product page rather than proof for this offer.
      caseStudyPath: null,
    },
    faqs: [],
    related: ["odoo-for-commerce-and-payments", "ops-and-erp-fit-sprint"],
    note: null,
  },

  {
    num: "10",
    id: "odoo-mep",
    slug: "odoo-for-mep-contractors",
    name: "Odoo for MEP & Engineering Contractors",
    group: "C",
    displayGroup: "odoo",
    industry: "construction",
    live: true,
    servicePage: "/Odoo/implementationPartner",
    h1:
      "Multi-currency procurement and audit-ready BOQs for contractors — live in eight weeks",
    metaTitle: "Odoo for MEP & Engineering Contractors | Visiomate",
    metaDescription:
      "Multi-currency procurement with three-tier approvals, standardised Bills of Quantity and project costing for MEP and engineering contractors. Eight weeks.",
    oneLiner:
      "Multi-currency procurement, three-tier approvals and audit-ready BOQs for your contracting business — live in eight weeks.",
    oneLiners: [
      "Multi-currency procurement, three-tier approvals and audit-ready BOQs for your contracting business — live in eight weeks.",
    ],
    whoFor:
      "MEP, engineering and specialist contractors running procurement-heavy projects across currencies.",
    timeline: "8 weeks",
    timelineShort: "8 weeks",
    price: "from $15,000",
    websiteDisplay: "from $15,000",
    priceDisplay: "from $15,000",
    priceShort: "from $15,000",
    priceDetail: null,
    symptoms: [
      "Purchase orders get approved in inboxes and you find out the value afterwards",
      "You buy in three currencies and the project cost depends on who did the maths",
      "Every bill of quantities is formatted differently by whoever built it",
      "Tender documents live in one person's folders",
      "You know whether a project made money only once it is finished",
    ],
    included: [
      "Multi-currency procurement with a three-tier approval hierarchy",
      "Standardised Bills of Quantity and Statements of Account",
      "Bidder and tender workflow",
      "Project costing tied to procurement and site delivery",
      "Real-time financial transparency for leadership",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary:
        "EME Aquatic Systems (Abu Dhabi) — 3-stage procurement security, 100% of paperwork digitised, real-time financial transparency",
      client: null,
      metrics: [],
      caseStudyPath: "/caseStudies/projects/emeAquaticSystems",
    },
    faqs: [],
    related: ["field-service-installer-platform", "ops-and-erp-fit-sprint"],
    note: null,
  },

  {
    num: "11",
    id: "odoo-events",
    slug: "odoo-for-events-and-production",
    name: "Odoo for Events & Production Companies",
    group: "C",
    displayGroup: "odoo",
    industry: "events",
    industryPages: [{ href: "/industries/events-production", label: "Events & Production" }],
    live: true,
    servicePage: "/Odoo/implementationPartner",
    h1:
      "Live job costing with margin alerts before an event loses money — live in eight weeks",
    metaTitle: "Odoo for Events & Production Companies | Visiomate",
    metaDescription:
      "Real-time project costing with live margin alerts, automated intake, and change orders tracked and billed. Built for AV production and event management.",
    oneLiner:
      "Real-time job costing with margin alerts that fire before an event loses money — live in eight weeks.",
    oneLiners: [
      "Real-time job costing with margin alerts that fire before an event loses money — live in eight weeks.",
    ],
    whoFor:
      "AV production, event management and premium rental businesses running many concurrent jobs.",
    timeline: "8 weeks",
    timelineShort: "8 weeks",
    price: "from $16,000",
    websiteDisplay: "from $16,000",
    priceDisplay: "from $16,000",
    priceShort: "from $16,000",
    priceDetail: null,
    symptoms: [
      "An enquiry arrives by phone and is re-typed into three places before it is a job",
      "You are running twenty jobs and can only see margin on the ones that finished",
      "Kit gets added on site and never reaches the invoice",
      "Every project manager tracks their own jobs their own way",
      "You find out a show lost money after the crew has gone home",
    ],
    included: [
      "Automated intake from phone systems and web forms",
      "One-click project creation with tasks populated from sales data",
      "Real-time project costing with live margin alerts",
      "Change orders tracked, approved and billed without leakage",
      "Tiered project-manager and executive dashboards",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary:
        'Centric Events (US) — guaranteed 50% margins via "margin killer" alerts, 100% data integrity lead to invoice, 100% of change orders tracked and billed · Muscat Media Group (Oman)',
      client: null,
      metrics: [],
      caseStudyPath: "/caseStudies/projects/muscatMediaGroup",
    },
    faqs: [],
    related: ["ai-quote-and-document-automation", "ops-and-erp-fit-sprint"],
    note: null,
  },

  // ── D. Platforms ──────────────────────────────────────────────────────────
  {
    num: "12",
    id: "installer-platform",
    slug: "field-service-installer-platform",
    name: "Installer Operations Platform",
    group: "D",
    displayGroup: "flagship",
    industry: "field-service",
    industryPages: [{ href: "/industries/field-service", label: "Field Service" }],
    live: true,
    servicePage: "/services/mobiledevelopment",
    // Outcome + industry + timeline, per the offer-page spec. It was
    // "Installer Operations Platform — first call to final invoice in one
    // system", which is the product name and the outcome but names neither who
    // it is for nor when it lands. Offer pages are not yet indexed, so the h1 is
    // open; metaTitle and metaDescription are unchanged.
    h1:
      "First call to final invoice in one system — for installers, live in four to eight weeks",
    metaTitle: "Field Service Software for Installers | Live in 8 Weeks | Visiomate",
    // Price removed — this offer is gated. Was: "...installers. From $15,000."
    metaDescription:
      "Quoting, site sketching, dispatch, an installer mobile app and a customer portal — built for fencing, fire protection, pool and trade installers.",
    // Tiered. The singular line is Phase 1, which is what websiteDisplay quotes.
    // solutions.js previously carried the Full line here, which put $28,000
    // beside a "from $15,000" price. Both figures are right; they are tiers.
    // Prices removed from both tiers — this offer is gated.
    oneLiner:
      "Phase 1 — Lead capture, site surveys and quoting for your installation business, running in one system: live in four weeks.",
    oneLiners: [
      "Phase 1 — Lead capture, site surveys and quoting for your installation business, running in one system: live in four weeks.",
      "Full — Your whole installation business from first call to final invoice: quoting, scheduling, an installer app and a customer portal, live in eight weeks.",
    ],
    whoFor:
      "US and UK installation and field-service businesses at $3–20M revenue: fencing, fire protection, pools and water features, HVAC, roofing, solar, landscaping.",
    timeline: "Phase 1 · 4 weeks   |   Full · 8 weeks",
    timelineShort: "4–8 weeks",
    price: "Phase 1 from $15,000 · Full from $28,000 + care plan",
    websiteDisplay: "from $15,000",
    priceDisplay: "from $15,000",
    priceShort: "from $15,000",
    priceDetail:
      "Phase 1 from $15,000 (4 weeks). Full platform from $28,000 (8 weeks), plus a care plan from $800/month. Payment 40% at kickoff, 30% at midpoint, 30% at go-live.",
    symptoms: [
      "Quotes are built in a spreadsheet and every estimator does it differently",
      "Scheduling lives on a whiteboard and nobody outside the office can see it",
      "Crews call the office to find out where they're going",
      "Customers ring to ask when someone is arriving and nobody can tell them",
      "Change orders happen on site and never get billed",
      "You don't know your win rate",
    ],
    included: [
      "Phase 1: CRM covering lead, site survey, quote and approval",
      "Phase 1: precision sketching / site-planning tool",
      "Phase 1: Good/Better/Best quoting",
      "Full adds: dispatch and scheduling planner",
      "Full adds: installer mobile app with daily tasks, calendar and arrival notifications",
      "Full adds: customer self-service portal — e-signature, quote approval, online payment",
      "Full adds: invoicing and leadership dashboards",
    ],
    notIncluded: [
      "Odoo licence fees",
      "Hardware for crews",
      "Accounting migration from an existing package",
    ],
    process: [
      { step: "Week 1", title: "Discovery and process mapping", deliverable: "Current lead-to-invoice flow documented, configuration plan agreed" },
      { step: "Week 2", title: "CRM and lead-to-quote", deliverable: "Lead capture, site survey and quote records live in test" },
      { step: "Week 3", title: "Sketching and site planning", deliverable: "Precision sketch tool configured to your products" },
      { step: "Week 4", title: "Quoting and approvals", deliverable: "Good/Better/Best quoting with approval rules — Phase 1 complete" },
      { step: "Week 5", title: "Dispatch and scheduling", deliverable: "Scheduling planner live with crew assignment" },
      { step: "Week 6", title: "Installer mobile app", deliverable: "Crew app with tasks, calendar and arrival notifications" },
      { step: "Week 7", title: "Customer portal", deliverable: "E-signature, quote approval and online payment live" },
      { step: "Week 8", title: "Migration, training, go-live", deliverable: "Data migrated, teams trained, platform live with hypercare" },
    ],
    commitments: [
      "Live on the date agreed at kickoff, or we keep working at no extra cost",
      "Built on an engine we have already delivered three times",
      "30-day post-launch warranty",
    ],
    weNeed: [
      "A named project owner with authority to sign off configuration decisions",
      "Feedback within 48 hours at each review point",
      "Customer, product and pricing data by the date agreed at kickoff",
      "Two crew members available for one hour of app testing in week six",
    ],
    proof: {
      summary:
        "Big Jerry's Fencing (21 cities, 9 US states) — 40% faster lead-to-installation, 25% higher quote-to-win · ORR Protection (US) — dispatch planner, technician portal, installer app · EME Aquatic Systems",
      client: "Big Jerry's Fencing",
      metrics: [
        "40% faster lead-to-installation",
        "25% higher quote-to-win rate",
        "21 cities across 9 US states",
      ],
      caseStudyPath: "/caseStudies/projects/bigJerrysFencing",
    },
    faqs: [
      { q: "How is this different from ServiceTitan or Jobber?", a: "Those are subscriptions you adapt your business to, priced per seat forever. This is your system, configured to how you already sell and install, with no per-seat ceiling on growth. If an off-the-shelf product fits you well, we'll say so." },
      { q: "Should we start with Phase 1 or the full platform?", a: "Start with Phase 1 if quoting is your bottleneck — most installers find it is. Move to the full platform once quoting is stable and scheduling becomes the constraint." },
      { q: "Will our crews actually use the app?", a: "They use it when it makes their day easier — today's jobs, addresses, and a one-tap arrival notification. We test with two of your actual crew in week six precisely to catch this." },
      { q: "We're not a fencing company. Does this still apply?", a: "Yes. Fencing, fire protection and water-feature installation run the same underlying flow: survey, design, quote, schedule, install, invoice. We've delivered all three." },
      { q: "What does the care plan cover?", a: "Monitoring, backups, bug fixes with a response SLA, a monthly allowance of enhancement work, version upgrades and a quarterly review against the outcomes set at kickoff." },
    ],
    related: ["ai-voice-receptionist", "ops-and-erp-fit-sprint"],
    note: null,
  },

  {
    num: "13",
    id: "civic-campaign",
    slug: "civic-campaign-platform",
    name: "Civic Campaign Platform",
    group: "D",
    displayGroup: "flagship",
    industry: "civic",
    // PUBLISHED 25 AUGUST 2026, on the client's instruction, reversing the
    // exclusion in VISIOMATE-OFFERS-BUILD-SPEC.md ("Do NOT create a page for the
    // Civic Campaign Platform on this site... It will get a separate microsite").
    // The reversal is recorded in that spec and in CONTENT-ARCHITECTURE §0.
    //
    // See `note` below: two compliance items remain unwritten, and neither is
    // resolved by publishing this page.
    live: true,
    servicePage: null,
    h1:
      "A campaign site with donations, a live legislation tracker and a vote record — live in three weeks",
    // No price in the title: this offer is gated like every non-entry offer.
    metaTitle: "Civic Campaign Platform | Live in 3 Weeks | Visiomate",
    metaDescription:
      "A campaign platform for candidates for local US office: biography and issue pages, a live legislation tracker, meeting videos, vote history, endorsements and compliant donations. Live in three weeks.",
    oneLiner:
      "A campaign platform with donations, live legislation tracking, meeting videos, vote history and endorsements — live in three weeks for $6,000, then $200 a month.",
    oneLiners: [
      "A campaign platform with donations, live legislation tracking, meeting videos, vote history and endorsements — live in three weeks for $6,000, then $200 a month.",
    ],
    whoFor:
      "Candidates for US city council, school board, county and other local office; also sitting officials and civic organisations.",
    timeline: "3 weeks",
    timelineShort: "3 weeks",
    price: "$6,000 + $200/month",
    websiteDisplay: "$6,000 + $200/mo — exact",
    priceDisplay: "$6,000 + $200/month",
    priceShort: "$6,000 + $200/mo",
    priceDetail: null,
    symptoms: [
      "You are running for local office with a site that is a biography and nothing else",
      "Voters ask where you stand and you send them a PDF",
      "The council votes on things and nobody outside the room knows what happened",
      "Endorsements arrive by email and live in your inbox",
      "You need to take donations without falling foul of the rules",
    ],
    included: [
      "Campaign site with biography, platform and issue pages",
      "Live legislation tracker pulling ordinances and resolutions from the city's agenda system",
      "Meeting video library and vote-history record",
      '"How our city works" civic explainer section',
      "Endorsements flow, community forum and polls",
      "Donation integration via a compliant processor (ActBlue, Anedot or equivalent)",
      "Admin portal, plus blog and news publishing",
    ],
    // Exclusions written for publication. Every line is a boundary rather than a
    // capability, so none of them introduces a new claim. The election-law line
    // is deliberate and load-bearing: this offer sells software, not compliance
    // advice, and the page has to say so.
    notIncluded: [
      "Campaign strategy, messaging or political consulting",
      "Legal or compliance advice on election law — take that from your own counsel",
      "Payment processor fees, and merchant or committee onboarding with the processor",
      "Voter file, canvassing data or CRM licensing",
      "Content writing and ongoing updates after launch",
    ],
    // Left empty on purpose. A three-week week-by-week would be a delivery
    // commitment nobody has agreed, and inventing commercial terms is barred by
    // CONTENT-ARCHITECTURE §0. Thirteen other offers are in the same position and
    // the template suppresses the section rather than showing an empty spine.
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary: "Carvajal for Culver City (California) — carvajalforculvercity.com",
      // Named on instruction. The metric carries NO figure — none exists — so
      // op-proof renders it as a note beneath the stat grid rather than
      // inventing a numeral.
      client: "Carvajal for Culver City (California)",
      metrics: [
        "A live campaign platform for a candidate for local office in California",
      ],
      // The campaign site itself, verified live (HTTP 200, no redirects). This
      // is the only evidence this offer has, so the proof section links it
      // rather than asserting the client and showing nothing.
      liveUrl: "https://carvajalforculvercity.com/",
      // NULL AND STAYING NULL. caseStudies/products/donationCampaignManagement
      // resolves, but it is NOT this project: it was read in full and is a
      // generic donor-management product overview with no client, no logo and no
      // metric, and it never mentions elections, candidates or campaigns for
      // office. Carvajal has no case study route anywhere on the site, and the
      // proof construction never links a client's own website.
      caseStudyPath: null,
    },
    // Left empty. Writing FAQs for this offer means writing about donation
    // compliance and foreign-vendor scope, which is exactly what the unresolved
    // legal opinion below is for.
    faqs: [],
    related: ["practice-and-telehealth-platform", "online-course-platform"],
    note:
      "PUBLISHED 25 AUGUST 2026 on client instruction, reversing the BUILD-SPEC exclusion. TWO COMPLIANCE ITEMS REMAIN OPEN AND ARE NOT RESOLVED BY PUBLISHING: (1) a written opinion from a US election-law attorney on foreign-vendor scope under 52 U.S.C. \u00a7 30121, which bars foreign nationals from providing anything of value in connection with a US election; (2) written confirmation that donations route through a compliant third-party processor (ActBlue, Anedot or equivalent) and that Visiomate never takes custody of contributions. NEITHER EXISTS IN THIS REPOSITORY. Original note, retained: support load on an active campaign can exceed the $200 monthly fee, so track the first engagement closely before selling this widely.",
  },

  {
    num: "14",
    id: "practice-telehealth",
    slug: "practice-and-telehealth-platform",
    name: "Practice & Telehealth Platform",
    group: "D",
    displayGroup: "flagship",
    industry: "healthcare",
    industryPages: [{ href: "/industries/healthcare", label: "Healthcare" }],
    live: true,
    servicePage: "/services/webdevelopment",
    h1: "Practice & Telehealth Platform — booking, portal and billing in one system",
    // Price removed from the title — this offer is gated. Was:
    // "HIPAA-Aligned Practice & Telehealth Platform | From $20,000 | Visiomate".
    metaTitle: "HIPAA-Aligned Practice & Telehealth Platform | Visiomate",
    // Was: "...Live in ten weeks from $20,000."
    metaDescription:
      "Booking, patient portal, telehealth and automated billing in one HIPAA-aligned platform for private practices and clinics. Live in ten weeks.",
    // Was: "...live in ten weeks from $20,000."
    oneLiner:
      "Booking, patient portal, telehealth and billing in one HIPAA-aligned system for your practice — live in ten weeks.",
    oneLiners: [
      "Booking, patient portal, telehealth and billing in one HIPAA-aligned system for your practice — live in ten weeks.",
    ],
    whoFor:
      "US private practices and group clinics — psychology, allied health and specialist care.",
    timeline: "10 weeks",
    timelineShort: "10 weeks",
    price: "from $20,000",
    websiteDisplay: "from $20,000",
    priceDisplay: "from $20,000",
    priceShort: "from $20,000",
    priceDetail:
      "From $20,000. Payment 40% at kickoff, 30% at midpoint, 30% at go-live. Care plan from $800/month after launch.",
    symptoms: [
      "Front-desk staff spend their day on scheduling phone calls",
      "No-shows are eating a visible share of your revenue",
      "Intake forms are printed, scanned and re-typed",
      "Telehealth runs on a consumer video tool you're not sure is compliant",
      "Billing is a separate manual process at the end of each week",
    ],
    included: [
      "High-performance practice website",
      "Self-service booking engine with automated reminders",
      "HIPAA-aligned telehealth suite",
      "Patient portal with intake forms and records access",
      "Automated billing and a clinical analytics dashboard",
    ],
    notIncluded: [
      "Clearinghouse or payer contracts",
      "EHR replacement — we integrate rather than replace",
      "Legal or compliance certification",
    ],
    process: [
      { step: "Weeks 1–2", title: "Discovery and compliance mapping", deliverable: "Workflow map, data-handling review, configuration plan" },
      { step: "Weeks 3–4", title: "Website and booking engine", deliverable: "Practice site live in staging with self-service booking" },
      { step: "Weeks 5–6", title: "Patient portal and intake", deliverable: "Portal with digital intake forms and records access" },
      { step: "Week 7", title: "Telehealth suite", deliverable: "HIPAA-aligned video consultations integrated with scheduling" },
      { step: "Week 8", title: "Billing automation", deliverable: "Automated billing flows and analytics dashboard" },
      { step: "Week 9", title: "UAT and staff training", deliverable: "Acceptance testing signed off, clinical and admin staff trained" },
      { step: "Week 10", title: "Go-live and hypercare", deliverable: "Live with daily support through the first week" },
    ],
    commitments: [
      "Live on the date agreed at kickoff, or we keep working at no extra cost",
      "HIPAA-aligned architecture and workflows throughout",
      "30-day post-launch warranty",
    ],
    weNeed: [
      "A named practice owner or manager to sign off decisions",
      "Feedback within 48 hours at each review point",
      "Existing patient and scheduling data in any structured format",
      "Your BAA requirements confirmed before we handle any real data",
    ],
    proof: {
      summary:
        "Nurtureful Psychology (US) — 50% less admin time, 35% fewer no-shows, HIPAA-aligned workflow throughout",
      client: "Nurtureful Psychology",
      metrics: [
        "50% reduction in administrative time",
        "35% fewer no-shows",
        // Was "100% HIPAA-aligned workflow". Same defect as the web service
        // page: a percentage attached to a compliance state rather than to a
        // measured outcome. It also sat three sections above this offer's own
        // FAQ 1, which correctly says compliance "is an organisational status
        // you hold, not something a vendor can confer" — so the page both
        // claimed 100% and disclaimed the ability to confer it. The numeral is
        // gone; op-proof renders a figureless metric below the stat grid.
        "HIPAA-aligned architecture and workflows throughout",
      ],
      caseStudyPath: "/caseStudies/projects/nurturefulPsychology",
    },
    faqs: [
      { q: "Is this HIPAA compliant?", a: "We build HIPAA-aligned architecture and workflows — encryption, access controls, audit logging and a signed BAA where we handle PHI. Compliance itself is an organisational status you hold, not something a vendor can confer. We build so that you can meet it." },
      { q: "Do we have to replace our EHR?", a: "No. We integrate with what you run wherever an interface exists, and replace only what you actually want replaced." },
      { q: "Can it handle multiple clinicians?", a: "Yes — individual calendars, availability rules and per-clinician reporting are part of the standard build." },
      { q: "What happens to our existing patient data?", a: "It's migrated as part of week nine, under your BAA, with a validation pass before go-live." },
      { q: "Ten weeks feels long. Can it be faster?", a: "Booking and the website can go live sooner as a phased launch. We'd rather stage it than rush compliance-sensitive work." },
    ],
    related: ["ops-and-erp-fit-sprint", "ai-voice-receptionist"],
    note: null,
  },

  {
    num: "17",
    id: "online-course-platform",
    slug: "online-course-platform",
    name: "Online Course & Certification Platform",
    group: "D",
    displayGroup: "flagship",
    industry: "education",
    live: true,
    servicePage: "/services/webdevelopment",
    h1:
      "Courses, enrolment, payment and certificates in one platform — live in five to eight weeks",
    metaTitle: "Online Course & Certification Platform | Visiomate",
    metaDescription:
      "Course authoring, learner enrolment and payment, automated assessment and certificates issued on completion. For training providers and professional bodies.",
    oneLiner:
      "Core — A platform where you build courses, learners enrol and pay, complete the material and receive a certificate: live in five weeks.",
    oneLiners: [
      "Core — A platform where you build courses, learners enrol and pay, complete the material and receive a certificate: live in five weeks.",
      "Complete — A full accredited-education business: assessments, credit tracking, course bundles, subscriptions and accreditation reporting, live in eight weeks.",
    ],
    whoFor:
      "Training providers, professional bodies, continuing-education platforms and accredited course publishers selling to US, UK and EU learners.",
    timeline: "Core 5 weeks · Complete 8 weeks",
    timelineShort: "5–8 weeks",
    price: "Core from $12,000 · Complete from $18,000",
    websiteDisplay: "from $12,000",
    priceDisplay: "from $12,000",
    priceShort: "from $12,000",
    priceDetail: null,
    symptoms: [
      "Learners enrol by email and someone types them into a spreadsheet",
      "You issue certificates by hand and cannot prove who earned what",
      "Payment happens in one place and access is granted in another",
      "You cannot tell which module learners abandon",
      "Your accreditation body wants credit records you have to assemble by hand",
    ],
    included: [
      "Core: course authoring for your own team — modules, video, documents, quizzes",
      "Core: learner registration, catalogue and enrolment",
      "Core: payments and checkout, single course or bundle",
      "Core: progress tracking and completion rules",
      "Core: automated evaluation and pass/fail scoring",
      "Core: certificate generation, issued automatically on completion",
      "Complete adds: credit tracking and accreditation reporting (CE, CPD or equivalent)",
      "Complete adds: subscriptions and membership access",
      "Complete adds: instructor accounts and revenue splits",
      "Complete adds: learner analytics and drop-off reporting",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary: "CONFIRM CLIENT NAME — delivered course, evaluation and certification platform",
      client: null,
      metrics: [],
      // /caseStudies/products/classManagement/ resolves, but it was read in full
      // and names no client, no logo and no metric. It is a product page for a
      // class-management solution, not a case study, and it cannot confirm this
      // offer's client. Do not link it here.
      caseStudyPath: null,
    },
    faqs: [],
    related: ["practice-and-telehealth-platform", "odoo-for-commerce-and-payments"],
    needsClientConfirmation: true,
    note:
      "BLOCKING: the proof line needs the client name confirmed before this goes into any client-facing material. classManagement is NOT that client — it names nobody.",
  },

  // ── E. Capacity ───────────────────────────────────────────────────────────
  {
    num: "15",
    id: "care-plan",
    slug: "care-plan",
    name: "Care Plan",
    group: "E",
    displayGroup: "capacity",
    industry: "all",
    live: true,
    servicePage: null,
    h1:
      "The team that built your system keeps it running — monthly, rolling",
    metaTitle: "Care Plan | Ongoing Support After Go-Live | Visiomate",
    metaDescription:
      "Monitoring, backups, bug fixes with a response SLA, a monthly allowance of enhancement work and version upgrades, from the team that built your system.",
    oneLiner:
      "The team that built your system keeps it running, monitored and improving.",
    oneLiners: [
      "The team that built your system keeps it running, monitored and improving.",
    ],
    whoFor: "Every client after go-live. Attached to all build offers by default.",
    timeline: "Monthly, rolling",
    timelineShort: "Monthly",
    price: "from $800/month",
    websiteDisplay: "from $800/mo",
    priceDisplay: "from $800/month",
    priceShort: "from $800/mo",
    priceDetail: null,
    // An attach-on rather than something a buyer arrives with, so these read
    // closer to reasons than to symptoms. Flagged for review at page build.
    symptoms: [
      "The system went live and the people who built it moved on",
      "Nobody is watching whether backups actually ran",
      "A small change means finding a vendor and scoping a project",
      "You are versions behind and unsure what upgrading breaks",
      "When something breaks there is no agreed response time",
    ],
    included: [
      "Monitoring, backups and uptime management",
      "Bug fixes and a defined support-response SLA",
      "A monthly allowance of enhancement work",
      "Version upgrades and security patching",
      "Quarterly review against the outcomes we set at kickoff",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary:
        "Multi-year support relationships with ORR Protection, Big Jerry's and other US clients",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [],
    related: ["development-month", "embedded-delivery-pod"],
    note: null,
  },

  {
    num: "16",
    id: "delivery-pod",
    slug: "embedded-delivery-pod",
    name: "Embedded Delivery Pod",
    group: "E",
    displayGroup: "capacity",
    industry: "software",
    live: true,
    servicePage: "/services/staffaugmentation",
    h1:
      "A senior engineer, a mid-level engineer and QA embedded in your team — rolling monthly",
    metaTitle: "Embedded Delivery Pod | Dedicated Team, Monthly | Visiomate",
    metaDescription:
      "A senior engineer, a mid-level engineer and a QA specialist embedded in your team, with project management and founder oversight. Scale at 30 days' notice.",
    oneLiner:
      "A senior engineer, a mid-level engineer and QA embedded in your team with founder oversight, starting within a week.",
    oneLiners: [
      "A senior engineer, a mid-level engineer and QA embedded in your team with founder oversight, starting within a week.",
    ],
    whoFor:
      "Product companies and scale-ups needing durable capacity rather than a fixed-scope project.",
    timeline: "Rolling monthly, 4-week sprints",
    timelineShort: "Rolling monthly",
    price: "from $11,000/month",
    websiteDisplay: "from $11,000/mo",
    priceDisplay: "from $11,000/month",
    priceShort: "from $11,000/mo",
    priceDetail: null,
    symptoms: [
      "Your roadmap needs a team, not a project",
      "Every contractor you add needs managing by someone you cannot spare",
      "Work ships without anyone testing it properly",
      "You need to grow the team this quarter and shrink it next",
      "You want senior attention, not whoever is on the bench",
    ],
    included: [
      "One senior engineer, one mid-level engineer and a QA specialist",
      "Project-management oversight and sprint reporting",
      "Founder involvement throughout the engagement",
      "Overlap with US and UK working hours",
      "Scale the pod up or down at 30 days' notice",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary:
        "Long-running embedded engagements with Net Atlantic, ORR Safety and other US clients",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [],
    related: ["development-month", "platform-modernization-assessment"],
    note: null,
  },

  {
    num: "19",
    id: "development-month",
    slug: "development-month",
    name: "Development Month",
    group: "E",
    displayGroup: "capacity",
    industry: "all",
    live: true,
    servicePage: "/services/staffaugmentation",
    h1:
      "A full month of development capacity in the discipline you need — starting within a week",
    metaTitle: "Development Month | Dedicated Capacity, Monthly | Visiomate",
    metaDescription:
      "A full month of delivered development in backend, frontend, mobile or Odoo, against your backlog and in your tools. Starts within a week, three-month minimum.",
    oneLiner:
      "A full month of development capacity in the discipline you need — backend, frontend, mobile or Odoo — on a three-month commitment.",
    oneLiners: [
      "A full month of development capacity in the discipline you need — backend, frontend, mobile or Odoo — on a three-month commitment.",
      "You are buying a month of delivered development, not hiring a named person.",
    ],
    whoFor:
      "Clients with a steady backlog who want predictable capacity without a hiring process, and existing clients whose change requests do not justify a fixed-scope offer.",
    timeline: "Starts within one week · 3-month minimum commitment",
    timelineShort: "Monthly, 3-month minimum",
    price:
      "Backend, frontend or mobile $3,000/month · Odoo $3,500/month · three-month minimum",
    websiteDisplay: "$3,000/month",
    priceDisplay: "$3,000/month",
    priceShort: "$3,000/mo",
    priceDetail: null,
    // As with the Care Plan, this is capacity rather than a problem someone
    // arrives with. Review the section heading when the page is built.
    symptoms: [
      "Your backlog grows faster than you can clear it",
      "Hiring takes months and you need the work done now",
      "The changes you need are too small to scope as projects and too many to ignore",
      "You need this month's work in backend and next month's in mobile",
      "You want the work on your board, in your tools",
    ],
    included: [
      "One full development month in your chosen discipline",
      "Choose backend, frontend, mobile or Odoo — switch discipline between months",
      "Work delivered against your backlog, in your tools, on your board",
      "Weekly progress reporting",
      "Overlap with US and UK working hours",
      "Three-month minimum; month-to-month available at a higher monthly rate",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary: "The delivery model behind our long-running US engagements",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [],
    related: ["embedded-delivery-pod", "care-plan"],
    note:
      "Scope guard: this is one development month only. It does not include project management, QA or founder oversight — those belong to the Embedded Delivery Pod (offer 16). Never express this offer in hours; a published hourly figure puts us on a global rate table we cannot win from Lahore.",
  },

  {
    num: "20",
    id: "d365-support",
    slug: "dynamics-365-support",
    name: "Dynamics 365 Support & Continuing Development",
    group: "E",
    displayGroup: "capacity",
    industry: "all",
    // Featured on two industry pages: Distribution's primary offer, and
    // Manufacturing's second offer (for shop floors already on D365).
    industryPages: [
      { href: "/industries/distribution", label: "Distribution" },
      { href: "/industries/manufacturing", label: "Manufacturing" },
    ],
    live: true,
    servicePage: "/dynamics365/financeOperations",
    h1:
      "A named engineer on the Dynamics 365 you already run — maintained and extended, monthly",
    metaTitle:
      "Dynamics 365 Support & Continuing Development | Monthly | Visiomate",
    // No price in the title: this offer is gated.
    metaDescription:
      "Maintenance and continuing development for a live Dynamics 365 environment. A named engineer, 160 hours a month, incident response and update regression checks.",
    oneLiner:
      "Maintenance and continuing development for the Dynamics 365 you already run — a named engineer who knows your environment, 160 hours a month.",
    oneLiners: [
      "Maintenance and continuing development for the Dynamics 365 you already run — a named engineer who knows your environment, 160 hours a month.",
    ],
    whoFor:
      "Businesses already running Microsoft Dynamics 365 who need it maintained and extended, without hiring a permanent D365 engineer. Not an implementation and not a rescue — this is for a system that is live and in use.",
    timeline: "Monthly, 3-month minimum",
    timelineShort: "Monthly",
    price: "from $2,800/month for 160 hours",
    websiteDisplay: "from $2,800/mo",
    priceDisplay: "from $2,800/month",
    priceShort: "from $2,800/mo",
    priceDetail:
      "From $2,800/month for 160 hours of maintenance and continuing development, on a three-month minimum. A $2,800 environment assessment runs first.",
    // Carried verbatim from src/docs/offers-data.js. Every one is about a system
    // that is already live: none of them reads as a pre-implementation symptom.
    symptoms: [
      "Every change to Dynamics waits on a consultant who has to relearn your setup first",
      "A Microsoft update lands and something in your customisations quietly stops working",
      "Your finance team exports to Excel because the report they need was never built",
      "The person who configured your environment has left and nothing was documented",
      "Small improvements never happen because there is no one to do them",
      "You are paying for modules nobody has opened in a year",
    ],
    included: [
      "A named engineer who learns your environment and stays on it",
      "160 hours a month of maintenance and continuing development",
      "Incident response against agreed severity tiers",
      "Configuration changes, new reports and dashboards",
      "Integration maintenance as connected systems change",
      "Regression checks against your customisations when Microsoft ships an update",
      "A monthly review of what was done and what is queued",
      // THE UNUSED-HOURS LINE IS DELIBERATELY ABSENT. offers-data.js proposed
      // "unused hours roll over for up to two months"; the client has not chosen
      // between no rollover, a two-month carry and a quarterly reset, so no rule
      // is stated rather than the wrong one. Add one line here once decided.
    ],
    notIncluded: [
      // Coverage is CONFIRMED as business hours, eight hours a day. It is stated
      // as an exclusion because /dynamics365/financeOperations/ currently claims
      // around-the-clock critical response, and this is the offer that has to be
      // honest about it. See the note below.
      "Cover outside business hours — support runs eight hours a day on business days",
      "New module implementations — scoped and quoted separately",
      "Microsoft licence costs, paid directly to Microsoft",
      "Raising and managing support tickets with Microsoft on your behalf",
      "Version upgrade projects",
    ],
    // NOT a week-by-week. This is a rolling monthly engagement, so the `step`
    // field carries a named phase where a week number would otherwise sit, and
    // processHeading/processLede override the template's build-project wording.
    process: [
      { step: "Before cover starts", title: "Environment assessment", deliverable: "A written picture of your instance, customisations, integrations and outstanding issues" },
      { step: "Getting started", title: "Onboarding your named engineer", deliverable: "Access provisioned, your environment learned, the first month's work prioritised" },
      { step: "Every month", title: "The working cycle", deliverable: "Incidents answered, changes and reports built, integrations kept working" },
      { step: "End of each month", title: "The review", deliverable: "What was done, what it cost in hours, and what is queued next" },
    ],
    processHeading: "Four phases, and the last one repeats every month",
    processLede:
      "Nothing is delivered once here — this is cover that starts and then continues, so the last phase comes round again rather than finishing.",
    commitments: [
      "A named engineer who knows your environment, not whoever is free that week",
      "160 hours a month, tracked and reported against what they were spent on",
      "Every Microsoft update regression-checked against your customisations",
      "A written review at the end of every month",
    ],
    weNeed: [
      "Access to your Dynamics 365 environment and a sandbox to test in",
      "A named contact who can prioritise what the month's hours go on",
      "Feedback within 48 hours at each review point",
      "Whatever documentation and customisation history exists, however thin",
    ],
    // No proof section renders: `client` is null, so the template's showProof
    // guard suppresses it. The candidate in offers-data.js is
    // caseStudies/products/mD365BulkStockDistribution, which was read in full and
    // names NO client and carries NO metric: it is a product overview, not a case
    // study, so it cannot evidence this offer. caseStudyPath stays null.
    proof: {
      summary:
        "CONFIRM — candidate: D365 Bulk Stock Distribution. Verify the client and metric before publishing.",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [
      { q: "We already run Dynamics 365. Is this for us, or is it an implementation?", a: "This is for a system that is already live and in use. There is no configuration project and no go-live: the engineer picks up the environment as it stands and maintains and extends it from there. If your rollout never actually landed, the ERP Rescue Sprint is the honest starting point instead." },
      { q: "What happens when Microsoft ships an update?", a: "Every release wave and routine patch is regression-checked against your customisations before it reaches production, in a sandbox rather than on your live environment. That check is part of the monthly hours, not a separate quote." },
      { q: "Can you take over an environment somebody else built?", a: "Yes, and it is the normal case. The environment assessment exists for exactly that: it establishes what you actually have, what was customised and what is already broken, before anyone commits to maintaining it. There is no requirement that the original implementation came from here." },
      { q: "What do the hours actually get spent on?", a: "Whatever you prioritise: incidents, configuration changes, reports and dashboards that were never built, integration maintenance as the systems around Dynamics change. The monthly review records where they went, so the split between keeping the lights on and improving things stays visible rather than assumed." },
      { q: "How is this different from hiring a Dynamics contractor?", a: "Continuity. A contractor relearns your setup each engagement and leaves with what they learned; a named engineer on a rolling month accumulates that knowledge and it stays available to you. It is also why there is a three-month minimum, because the first month goes largely on learning the environment." },
    ],
    related: [
      "platform-modernization-assessment",
      "erp-rescue-sprint",
      "development-month",
    ],
    note:
      "TWO ITEMS STILL OPEN. (1) Unused-hours rule: client is weighing no rollover, a two-month carry, or a quarterly reset. No rule is stated on the page until one is chosen. (2) Proof client unconfirmed; mD365BulkStockDistribution names no client and carries no metric, so no proof section renders. CONFIRMED: $2,800/month for 160 hours (client set this on 25 August 2026, down from the $3,800 originally proposed — see the margin note below), and coverage is business hours eight hours a day, NOT 24/7. That contradicts /dynamics365/financeOperations/, which claims critical response within one hour around the clock. That page is over-promising until corrected. MARGIN: offers-data.js recorded 160 hours at an internal cost of ~$1,500, needing $3,000 to clear the 2.0x floor and $3,750 for 60% margin. At $2,800 this offer sits BELOW that 2.0x floor, at roughly 1.87x. Flagged, not blocked — the client set the figure. Note also that the monthly fee and the environment assessment are now the same $2,800.",
  },

  // ── F. Product ────────────────────────────────────────────────────────────
  {
    num: "18",
    id: "managed-living",
    slug: "managed-living-platform",
    name: "Managed Living Platform",
    group: "F",
    displayGroup: "product",
    industry: "property",
    industryPages: [
      { href: "/industries/real-estate", label: "Real Estate / Property Management" },
    ],
    live: true,
    servicePage: null,
    h1:
      "Every resident service request in one app, for managed buildings — live the same day",
    metaTitle: "Managed Living Platform | Live the Same Day | Visiomate",
    metaDescription:
      "A resident app for every managed service, with scheduling, SLA tracking and chargeable-service billing. For gated communities and serviced apartments.",
    oneLiner:
      "A single app through which residents of a luxury housing scheme or apartment building request every managed service — housekeeping, medical assistance, guest reception, recreation bookings and more — and through which management tracks, assigns and reports on all of it.",
    oneLiners: [
      "A single app through which residents of a luxury housing scheme or apartment building request every managed service — housekeeping, medical assistance, guest reception, recreation bookings and more — and through which management tracks, assigns and reports on all of it.",
      "A $2,500 one-time onboarding fee, then live the same day — whether you run seven apartments or a thousand.",
    ],
    whoFor:
      "Owners and operators of luxury housing schemes, gated communities, serviced apartments and managed residential towers in the Gulf, UK and US — from a seven-unit block to a thousand-unit portfolio.",
    timeline: "Live the same day",
    timelineShort: "Same day",
    price:
      "$2,500 onboarding (one-time), $5 per unit per month, plus $3 per camera for the surveillance module add-on.",
    websiteDisplay: "$5/unit/mo + $2,500 onboarding",
    priceDisplay: "$5 per unit per month",
    priceShort: "$5/unit/mo",
    priceDetail:
      "$2,500 onboarding, one-time. Surveillance module (add-on): $3 per camera.",
    symptoms: [
      "Residents request everything by message and calls to the front desk",
      "Nobody can say how long a request has been open or who has it",
      "Guests arrive and the gate has no record of them",
      "Amenity bookings are a paper diary at reception",
      "Chargeable services get delivered and never get billed",
      "Your owners' association asks for numbers you have to reconstruct",
    ],
    included: [
      "Resident mobile app for raising and tracking any service request",
      "Housekeeping and maintenance scheduling with assignment to staff",
      "Medical assistance requests with escalation routing",
      "Guest reception, visitor pre-authorisation and gate notification",
      "Recreational facility and amenity booking",
      "Announcements, community notices and document library",
      "Management console — SLA tracking, staff workload, request ageing",
      "Billing and chargeable-service invoicing to residents",
      "Reporting for the operator or owners' association",
      "Your branding, your domain, your service catalogue",
      "Self-serve signup, instant provisioning",
    ],
    notIncluded: [],
    process: [],
    commitments: [],
    weNeed: [],
    proof: {
      summary: "Deployed residential managed-services product (internally: Tenant App)",
      client: null,
      metrics: [],
      caseStudyPath: null,
    },
    faqs: [],
    related: [],
    note:
      "This is a PRODUCT, not a project. It is sold as a subscription, not scoped per client. Resist custom development requests — route them to a Development Month (offer 19) or decline. The moment it is customised per community, the margin and the roadmap are gone.",
  },
]

const getOffer = slug => offers.find(o => o.slug === slug)
const liveOffers = () => offers.filter(o => o.live)

// ─────────────────────────────────────────────────────────────────────────────
// THE PRICING GATE — which offers have one.
//
// The figure is NEVER gated. Every offer shows its "from $X" on /pricing/, on
// /solutions/, on its own page and in the service-page blocks. What sits behind
// the form is the detail: payment schedule, tier breakdown and exclusions.
//
// These three are not gated at all. They are the paid front door — a form in
// front of a $1,200 diagnostic defeats the point of publishing it — and the
// whole funnel depends on them being frictionless.
//
// Listed by slug, not by `displayGroup === "entry"`. A group test would
// silently un-gate any offer later filed under entry, and that is the kind of
// change nobody notices until a price has been given away.
// ─────────────────────────────────────────────────────────────────────────────
const ENTRY_SLUGS = [
  "ops-and-erp-fit-sprint",
  "erp-rescue-sprint",
  "platform-modernization-assessment",
]

const isGated = offer => !ENTRY_SLUGS.includes(offer.slug)

module.exports = { offers, getOffer, liveOffers, isGated, ENTRY_SLUGS }
